import styled, { css } from 'styled-components';

export const PageWrapper = styled.div`
  margin: .2em -2em;
  margin-bottom: -8em;
  padding: 1rem 2em calc(2rem + 8em);
  background: #f7f8fa;
  min-height: calc(100vh - 4em - 2px);

  @media (max-width: 768px) {
    margin: -1em;
    margin-bottom: -4em;
    padding: 1rem 1em calc(2rem + 4em);
    min-height: calc(100vh - 60px);
  }

  @media (max-width: 480px) {
    margin: -0.8em;
    margin-bottom: -3em;
    padding: 1rem 0.8em calc(2rem + 3em);
    min-height: calc(100vh - 56px);
  }
`;

export const PageHeader = styled.div`
  margin-bottom: 1rem;
`;

export const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #2b313c;
  margin: 0;
`;

export const PageToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const EstateFilterRow = styled.div`
  width: 280px;
  max-width: 100%;
`;

export const MasterDetailLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 38%) 1fr;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.div`
  background: #fff;
  border: 1px solid rgba(34, 36, 38, 0.12);
  border-radius: 8px;
  padding: 1.25rem;
  min-height: 420px;
`;

const panelTitleBase = css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  color: #6435c9;
  margin-bottom: 1rem;

  .icon {
    margin: 0;
  }
`;

export const PanelTitle = styled.div`
  ${panelTitleBase}
`;

export const CardsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const UnitCardContainer = styled.div<{
  $isSelected?: boolean;
  $isInactive?: boolean;
  $isSelectable?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 8px;
  background: #fff;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  opacity: ${({ $isInactive }) => ($isInactive ? 0.72 : 1)};

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      border: 2px solid ${props => props.theme.main.color};
      box-shadow: 0 0 0 1px rgba(100, 53, 201, 0.08);
    `}

  &:hover {
    ${({ $isSelectable, $isSelected }) =>
    $isSelectable &&
    !$isSelected &&
    css`
        border-color: rgba(100, 53, 201, 0.45);
      `}
  }
`;

export const UnitCardMain = styled.button<{
  $isSelectable?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${props => props.theme.main.color};
    outline-offset: 2px;
    border-radius: 6px;
  }
`;

export const UnitCardMainStatic = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`;

export const UnitCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f4f4f5;
  color: #767676;
  flex-shrink: 0;

  .icon {
    margin: 0;
    font-size: 1.25rem;
  }
`;

export const UnitCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const UnitCardName = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #2b313c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UnitCardLocation = styled.div`
  font-size: 0.85rem;
  color: #767676;
  margin-top: 0.15rem;
`;


export const UnitCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  margin-left: auto;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 0.15rem;
  }
`;

const unitCardActionBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  .icon {
    margin: 0;
    font-size: 1rem;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.main.color};
    outline-offset: 2px;
  }
`;

export const UnitCardLinkAction = styled.button`
  ${unitCardActionBase}
  color: ${props => props.theme.main.color};

  &:hover {
    background: rgba(89, 36, 155, 0.08);
    color: #4a1d82;
  }
`;

export const UnitCardDestructiveAction = styled.button`
  ${unitCardActionBase}
  color: #db2828;

  &:hover {
    background: rgba(219, 40, 40, 0.08);
    color: #b21e1e;
  }

  &:focus-visible {
    outline-color: #db2828;
  }
`;

export const SectionDivider = styled.div`
  margin: 1.25rem 0 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(34, 36, 38, 0.1);
`;

export const InactiveSectionTitle = styled.div`
  ${panelTitleBase}
  color: #767676;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
`;

export const EmptyState = styled.p`
  color: #767676;
  font-size: 0.95rem;
  margin: 2rem 0;
  text-align: center;
`;

export const EmptyUnitsMessage = styled.p`
  color: #767676;
  font-size: 0.9rem;
  margin: 0.5rem 0 0;
`;

export const PaginationWrapper = styled.div`
  margin-top: 1rem;
`;
